import type { MapFactory } from '../map-factory.model';
import {
  buildUaeMapFactoriesDemo,
  seedMapFactoryCapabilityIds,
} from './uae-factories.adapter';

/**
 * Aligns with {@link import('@indutrust/factory-model').MOCK_FACTORY_DEMO_LATITUDE}.
 * Sample factory 1 (Premier Composite Technologies) at Dubai Investment Park.
 */
const DEMO_FACTORY_LATITUDE = 24.9786301;
/**
 * Aligns with {@link import('@indutrust/factory-model').MOCK_FACTORY_DEMO_LONGITUDE}.
 */
const DEMO_FACTORY_LONGITUDE = 55.1685973;

/** Canonical default fixture for {@link MapFactory}. */
export const MOCK_MAP_FACTORY: MapFactory = {
  id: 1,
  company_id: 1,
  name: 'Premier Composite Technologies (PCT HQ)',
  industrial_area_id: 2,
  latitude: DEMO_FACTORY_LATITUDE,
  longitude: DEMO_FACTORY_LONGITUDE,
  verified: true,
  // Matches {@link import('@indutrust/factory-model').MOCK_FACTORY_PUBLISHED}.
  capability_ids: [11, 12],
};

/** Returns a {@link MapFactory} merged with the supplied overrides. */
export function createMockMapFactory(
  overrides: Partial<MapFactory> = {}
): MapFactory {
  return { ...MOCK_MAP_FACTORY, ...overrides };
}

/**
 * Returns a list of unique {@link MapFactory} fixtures.
 * @param count - Number of fixtures to create. Defaults to `3`.
 * @param overrideAt - Optional callback returning per-index overrides.
 */
export function createMockMapFactoryList(
  count = 3,
  overrideAt?: (index: number) => Partial<MapFactory>
): MapFactory[] {
  return Array.from({ length: count }, (_, index) =>
    createMockMapFactory({
      id: index + 1,
      name: `Mock Factory ${index + 1}`,
      capability_ids: seedMapFactoryCapabilityIds(index + 1),
      ...overrideAt?.(index),
    })
  );
}

/**
 * UAE factory pins from `samples/factories.json` (adapted into
 * `uae-factories.sample.json`). Real plant names and coordinates; area ids
 * reference {@link import('./map-region.mock').MOCK_MAP_REGIONS_DEMO}.
 *
 * Used as the default seed for {@link mapFactoryHandlers} in the demo build.
 * The first pin ({@link MOCK_MAP_FACTORY}, id `1`) is the owned showcase
 * factory — aligned with
 * {@link import('@indutrust/factory-model').MOCK_FACTORY_PUBLISHED}.
 * Pin `43` ({@link import('@indutrust/factory-model').MOCK_AAE_FACTORY_ID},
 * Advanced Armour Engineering) is the complete discovery showcase with the
 * AAE virtual-tour graph. In demo mode the discovery map renders that pin
 * larger, brand-blue, and outside clusters so it is easy to find.
 *
 * `company_id: 1` on factory `1` matches
 * {@link import('@indutrust/shared-model').MOCK_COMPANY}
 * ("Gulf Precision Industries") and the search mock company hit so
 * `/map?company=1` deep links from search resolve to real pins in demo mode.
 *
 * Plant **names and locations** are real. Verification status, capabilities,
 * contacts, and other business fields attached in discovery profiles remain
 * invented demo data — not claims about those companies.
 */
export const MOCK_MAP_FACTORIES_DEMO: MapFactory[] = buildUaeMapFactoriesDemo();

export {
  DEMO_MAP_CAPABILITY_LEAF_IDS,
  seedMapFactoryCapabilityIds,
} from './uae-factories.adapter';
